/**
 * Amazon Advertising OAuth Utilities — Login with Amazon (LWA) (Story 19.13)
 *
 * Handles OAuth 2.0 + PKCE URL building, code exchange, refresh, and error
 * parsing against the Amazon Login with Amazon endpoints
 * (`https://www.amazon.com/ap/oa` for authorize, `https://api.amazon.com/auth/o2/token`
 * for token exchange / refresh).
 *
 * Amazon-specific characteristics that distinguish this from every other OAuth
 * integration in this codebase:
 *  - **HTTP Basic Authentication** for the token endpoint (matches Pinterest
 *    19.4 + Reddit 19.10, **diverges from Snapchat 19.7 / Microsoft Ads 19.1 /
 *    Klaviyo** which use form-body credentials). `client_id` is the username,
 *    `client_secret` is the password (RFC 7617). Sending credentials in BOTH
 *    the Basic header AND the body yields `invalid_client` per LWA spec.
 *  - **DOUBLE-COLON scope syntax (`advertising::campaign_management`)** —
 *    single-colon `advertising:campaign_management` triggers `unknown_scope`
 *    error per amzn/ads-advanced-tools-docs Discussion #161. This is the #1
 *    reported onboarding failure mode.
 *  - **Refresh tokens DO NOT rotate** — LWA's docs state refresh tokens are
 *    "valid indefinitely, unless the user has removed the website or mobile
 *    app from the list of allowed apps for their account". The refresh
 *    response does NOT include a `refresh_token` field. The adapter MUST
 *    carry forward the existing refresh_token unchanged. **Mirrors Reddit
 *    19.10 non-rotating pattern, inverts Snapchat 19.7 defensive-rotation.**
 *  - **180-day client-secret rotation** is enforced by Amazon at the
 *    application level. Refresh tokens **survive client-secret rotation** as
 *    long as `client_id` is unchanged.
 *  - **Aggressive 60-minute access token TTL** — `inlineRefreshOn401: true`
 *    is REQUIRED by the TD4 invariant (access TTL < 24h).
 *  - PKCE (code_challenge_method=S256) sent for defense-in-depth and uniformity
 *    with Klaviyo + Microsoft Ads + Pinterest + Snapchat + Reddit. LWA
 *    recommends PKCE but does not require it for confidential clients.
 *  - `redirect_uri` exact match — even trailing-slash differences fail with
 *    `invalid_request`.
 *  - Access tokens are prefixed `Atza|`, refresh tokens `Atzr|` (max 2048
 *    bytes). Prefix observability only — do NOT hard-fail on prefix mismatch.
 *  - Scope validation: if LWA silently narrows scope (returns `""` or omits
 *    `advertising::campaign_management`), the adapter fails loud with
 *    `scope-narrowed` and tokens are NOT persisted (silent scope-narrowing is
 *    a known OAuth attack vector).
 *
 * @see https://developer.amazon.com/docs/login-with-amazon/authorization-code-grant.html
 * @see https://developer.amazon.com/docs/login-with-amazon/refresh-token.html
 * @see https://developer-docs.amazon.com/sp-api/docs/lwa-credentials-faq
 * @see https://github.com/amzn/ads-advanced-tools-docs/discussions/161
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

/**
 * Amazon LWA authorize endpoint. Region-agnostic — the regional split applies
 * to the data API hosts, not the LWA token issuer. Use the `.com` endpoint
 * regardless of which Ads API region the user's profiles live in.
 */
const LWA_AUTHORIZE_URL = "https://www.amazon.com/ap/oa";

/**
 * Amazon LWA token endpoint. NA host serves all 3 Ads API regions for token
 * issuance. The `.co.uk` and `.co.jp` LWA endpoints exist but are for legacy
 * Amazon-specific flows, NOT Advertising API. **DO NOT use the regional LWA
 * endpoints** — only `api.amazon.com` is correct for Ads API integrations.
 */
const LWA_TOKEN_URL = "https://api.amazon.com/auth/o2/token";

const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;

/**
 * Scopes requested for Amazon Ads integration.
 *
 * **CRITICAL: DOUBLE colon (`advertising::campaign_management`)** — single
 * colon `advertising:campaign_management` triggers `unknown_scope` error per
 * amzn/ads-advanced-tools-docs Discussion #161.
 *
 * Other LWA scopes intentionally omitted from 19-13 scope:
 *   - `advertising::dsp_campaigns` — DSP-only (Story 19.16)
 *   - `advertising::account_management` — write campaigns (we never write)
 */
export const AMAZON_ADS_SCOPES = ["advertising::campaign_management"] as const;

/**
 * Amazon Ads regional API hosts. Profiles are partitioned by region; each
 * profile belongs to exactly one regional host. Discovery requires sequential
 * calls to all 3 hosts to enumerate a multi-region advertiser.
 *
 * Future-proof: adding a new region (e.g. AU/MX/IN if Amazon expands) is a
 * single entry here plus updating the `region` literal union — no other code
 * changes required.
 */
export const AMAZON_ADS_REGIONAL_HOSTS = {
  NA: "https://advertising-api.amazon.com",
  EU: "https://advertising-api-eu.amazon.com",
  FE: "https://advertising-api-fe.amazon.com",
} as const;

export type AmazonAdsRegion = keyof typeof AMAZON_ADS_REGIONAL_HOSTS;

// ============================================================================
// Types
// ============================================================================

export interface AmazonAdsAuthUrlOptions {
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
  /** PKCE code_challenge (Base64URL of SHA-256 of code_verifier) */
  codeChallenge: string;
}

export interface AmazonAdsTokenResponse {
  access_token: string;
  /** LWA only includes refresh_token on the INITIAL exchange. Refresh responses OMIT this field. */
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface AmazonAdsCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
  /** AC #14 distinct categories: lwa-app-draft-state, api-not-enabled, etc. */
  category?: string;
}

// ============================================================================
// LWA-specific token-shape sanitizer (AC #4.4 carry-forward)
// ============================================================================

/**
 * Round 2 Pre-mortem Failure E precision regex: matches Amazon LWA token-shaped
 * substrings (`Atza|...` or `Atzr|...`) where the post-prefix payload is ≥ 32
 * base64-style characters. Real LWA tokens are 100+ chars; the 32-char
 * threshold catches every real token while preserving coincidental prefix
 * collisions in user-provided strings (e.g. a sole-proprietor seller named
 * `"Atza|FreshBrewStore"` has only 14 chars after the prefix and is
 * deliberately NOT redacted).
 */
const LWA_TOKEN_SHAPE_REGEX = /Atz[ar]\|[A-Za-z0-9+/=_\-]{32,}/g;

/**
 * Wraps the base sanitizer with LWA-specific token-prefix redaction.
 *
 * The base `sanitizeErrorMessage` already redacts Bearer tokens and 40+-char
 * base64 strings, but the AC #4.4 invariant requires explicit guard against
 * `Atza|` / `Atzr|` prefixes plus protection of legitimate user content that
 * happens to start with the prefix. This function provides that precision
 * layer; it's idempotent (running twice yields the same output).
 */
export function sanitizeAmazonAdsErrorMessage(message: string): string {
  // Apply the LWA-specific shape regex BEFORE the base sanitizer so the
  // [REDACTED_LWA_TOKEN] marker survives downstream regex passes.
  const lwaRedacted = message.replace(LWA_TOKEN_SHAPE_REGEX, "[REDACTED_LWA_TOKEN]");
  return sanitizeErrorMessage(lwaRedacted);
}

// ============================================================================
// Build Authorization URL
// ============================================================================

/**
 * Builds the LWA OAuth 2.0 + PKCE authorization URL.
 *
 *   - Authorize URL is `https://www.amazon.com/ap/oa`
 *   - `scope` is space-joined per OAuth 2.0 spec (single value
 *     `advertising::campaign_management` here — DOUBLE COLON mandatory)
 *   - `code_challenge_method=S256` for defense-in-depth (LWA recommends PKCE
 *     on confidential clients).
 *   - `redirect_uri` MUST exactly match the LWA app's registered "Allowed
 *     Return URLs" — even trailing slash differences fail with
 *     `invalid_request` and no descriptive body.
 */
export function buildAmazonAdsAuthUrl(
  options: AmazonAdsAuthUrlOptions
): string {
  const { clientId, redirectUri, stateKey, codeChallenge } = options;

  if (!clientId) {
    throw new Error("AMAZON_ADS_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("AMAZON_ADS_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }
  if (!codeChallenge) {
    throw new Error("codeChallenge is required for PKCE");
  }

  const url = new URL(LWA_AUTHORIZE_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  url.searchParams.set("scope", AMAZON_ADS_SCOPES.join(" "));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", codeChallenge);

  return url.toString();
}

// ============================================================================
// Typed token-endpoint error (so adapter callers don't regex-match messages)
// ============================================================================

export interface AmazonAdsTokenErrorOptions {
  /** The verbatim value of the `error` field from LWA's JSON envelope */
  oauthError?: string;
}

export class AmazonAdsTokenError extends Error {
  readonly oauthError: string | undefined;
  constructor(message: string, options: AmazonAdsTokenErrorOptions = {}) {
    super(message);
    this.name = "AmazonAdsTokenError";
    this.oauthError = options.oauthError;
  }
}

/**
 * Pulls the OAuth `error` code out of a sanitized provider body.
 *
 * @internal exported for direct unit testing only
 */
export function extractOAuthErrorCode(
  providerBody: string | undefined
): string | undefined {
  if (!providerBody) return undefined;
  try {
    const parsed: unknown = JSON.parse(providerBody);
    if (parsed === null || typeof parsed !== "object") return undefined;
    const err = (parsed as Record<string, unknown>).error;
    if (typeof err === "string" && err.length > 0) {
      return err;
    }
    if (err !== null && typeof err === "object") {
      const nestedCode = (err as Record<string, unknown>).code;
      if (typeof nestedCode === "string" && nestedCode.length > 0) {
        return nestedCode;
      }
      const nestedError = (err as Record<string, unknown>).error;
      if (typeof nestedError === "string" && nestedError.length > 0) {
        return nestedError;
      }
    }
  } catch {
    // Not JSON — return undefined so callers fall back to the message body.
  }
  return undefined;
}

/**
 * Pulls the most descriptive message out of an LWA token error envelope, with
 * empty-string fallback when nothing structured is present.
 *
 * LWA's token-endpoint errors come back as the standard OAuth 2.0 form:
 *   `{ "error": "invalid_grant", "error_description": "..." }`
 */
export function extractOAuthErrorDescription(providerBody: string): string {
  try {
    const parsed = JSON.parse(providerBody) as {
      error?: unknown;
      error_description?: unknown;
      message?: unknown;
    };
    const err =
      typeof parsed.error === "string" && parsed.error.length > 0
        ? parsed.error
        : "";
    const desc =
      typeof parsed.error_description === "string" &&
      parsed.error_description.length > 0
        ? parsed.error_description
        : "";
    const msg =
      typeof parsed.message === "string" && parsed.message.length > 0
        ? parsed.message
        : "";

    if (err && desc) return `${err}: ${desc}`;
    if (desc) return desc;
    if (err) return err;
    if (msg) return msg;
    return "";
  } catch {
    return "";
  }
}

/**
 * Builds the HTTP Basic Authorization header value for LWA's token endpoint.
 *
 * LWA accepts credentials EITHER in the body OR via `Authorization: Basic
 * <base64(client_id:client_secret)>`. We use **HTTP Basic Auth** (matches
 * Pinterest 19.4 + Reddit 19.10, diverges from Snapchat 19.7 / MS Ads 19.1 /
 * Klaviyo). Basic Auth keeps the client_secret out of the URL-encoded body
 * where it could be captured by request-logging middleware.
 *
 * Sending credentials in BOTH the header AND the body yields `invalid_client`
 * per LWA spec — the body MUST omit `client_id` and `client_secret`.
 *
 * @internal exported for direct unit testing only
 */
export function buildBasicAuthHeader(
  clientId: string,
  clientSecret: string
): string {
  // btoa is a Cloudflare Workers global; it operates on Latin-1 strings.
  // client_id / client_secret from LWA are ASCII-only, so this is safe.
  return `Basic ${btoa(`${clientId}:${clientSecret}`)}`;
}

// ============================================================================
// Token Exchange
// ============================================================================

/**
 * Exchanges an authorization code for access + refresh tokens.
 *
 * CRITICAL: LWA uses HTTP Basic Authentication for the token endpoint.
 * `client_id` and `client_secret` go in the `Authorization: Basic ...` header,
 * NOT the body. This matches Pinterest / Reddit, **diverges from Snapchat /
 * Microsoft Ads / Klaviyo which use form-body credentials**. The body contains
 * `grant_type=authorization_code`, `code`, `redirect_uri`, and `code_verifier`.
 *
 * The returned token response is validated:
 *   1. `access_token` is a non-empty string (LWA emits `Atza|`-prefix tokens —
 *      observability-only check, not enforced).
 *   2. `refresh_token` is a non-empty string (initial exchange only — `Atzr|`-
 *      prefix observability-only).
 *   3. `expires_in` is a finite number (NaN-prevention for arithmetic).
 *   4. `scope` includes `advertising::campaign_management` — silent scope-
 *      narrowing is rejected loudly with a `scope-narrowed` error
 *      (AC #2 invariant).
 */
export async function exchangeAmazonAdsCode(
  code: string,
  codeVerifier: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<AmazonAdsTokenResponse> {
  // Body has grant_type, code, redirect_uri, code_verifier. NO client_id /
  // client_secret in the body — those go in the Basic auth header (sending
  // them in BOTH places yields invalid_client per LWA spec).
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const result = await safeFetch(
    LWA_TOKEN_URL,
    {
      method: "POST",
      headers: {
        Authorization: buildBasicAuthHeader(clientId, clientSecret),
        // LWA spec is explicit about the charset — RFC-compliant
        // application/x-www-form-urlencoded alone is also accepted, but the
        // spec example includes the charset; we send the exact spec form for
        // maximum compatibility.
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new AmazonAdsTokenError(
      sanitizeAmazonAdsErrorMessage(
        `Amazon Ads token exchange failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  const data = (await response.json()) as AmazonAdsTokenResponse;

  validateTokenResponse(data, /* requireRefreshToken */ true);
  validateScope(data.scope);
  // Token-prefix observability — log warning but accept tokens regardless.
  logTokenPrefixObservability(data);

  return data;
}

// ============================================================================
// Token Refresh
// ============================================================================

/**
 * Refreshes an Amazon Ads access token via the refresh_token grant.
 *
 * **CRITICAL: LWA refresh tokens DO NOT rotate.** The refresh response does
 * NOT include a `refresh_token` field by default (LWA's docs at
 * developer.amazon.com/docs/login-with-amazon/refresh-token state "Refresh
 * tokens are valid indefinitely, unless the user has removed the website or
 * mobile app from the list of allowed apps for their account").
 *
 * The caller (`amazonAdsAdapter.refreshToken`) MUST carry forward the existing
 * `credentials.refreshToken` unchanged on every refresh. **Mirrors Reddit
 * 19.10 non-rotating pattern, inverts Snapchat 19.7 defensive-rotation.**
 * Defensive future-proof: if LWA ever does start returning a refresh_token
 * field (e.g. in response to the 180-day client-secret rotation policy), the
 * adapter persists it.
 *
 * Authentication: HTTP Basic Auth (same as initial exchange).
 *
 * Refresh tokens **survive 180-day client-secret rotation** as long as
 * `client_id` is unchanged (per LWA Credentials FAQ). Operationally, secret
 * rotation is a one-line `wrangler secret put` and a deploy.
 */
export async function refreshAmazonAdsToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string
): Promise<AmazonAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  });

  const result = await safeFetch(
    LWA_TOKEN_URL,
    {
      method: "POST",
      headers: {
        Authorization: buildBasicAuthHeader(clientId, clientSecret),
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new AmazonAdsTokenError(
      sanitizeAmazonAdsErrorMessage(
        `Amazon Ads token refresh failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  const data = (await response.json()) as AmazonAdsTokenResponse;

  // refresh_token is OPTIONAL on the refresh path — LWA's documented behaviour
  // is to omit it. The adapter carries forward the existing one.
  validateTokenResponse(data, /* requireRefreshToken */ false);

  return data;
}

// ============================================================================
// Internal: token-response validation
// ============================================================================

/**
 * Validates that an LWA token response has the fields we require and that
 * they're the correct type. LWA documents `expires_in` as an integer but a
 * future schema drift (e.g., `"3600"` string) would silently break
 * `Date.now() + expires_in * 1000` — string-multiplication yields NaN. We
 * catch that here and throw a clear error instead.
 */
export function validateTokenResponse(
  data: AmazonAdsTokenResponse,
  requireRefreshToken: boolean
): void {
  if (typeof data.access_token !== "string" || data.access_token.length === 0) {
    throw new Error("Invalid Amazon Ads token response: missing access_token");
  }
  if (requireRefreshToken) {
    if (
      typeof data.refresh_token !== "string" ||
      data.refresh_token.length === 0
    ) {
      throw new Error(
        "Invalid Amazon Ads token response: missing refresh_token on initial exchange"
      );
    }
  }
  if (typeof data.expires_in !== "number" || !Number.isFinite(data.expires_in)) {
    throw new Error(
      `Invalid Amazon Ads token response: expires_in must be a finite number (received ${typeof data.expires_in})`
    );
  }
}

/**
 * Validates that the returned scope contains the requested
 * `advertising::campaign_management` scope. Silent scope-narrowing is a known
 * OAuth attack vector; if LWA returns `""` or omits our scope, fail loud and
 * never store under-scoped tokens (AC #2 invariant).
 *
 * LWA returns scope as a space-separated string (RFC 6749 §3.3).
 *
 * @internal exported for direct unit testing only
 */
export function validateScope(scope: string | undefined): void {
  if (typeof scope !== "string" || scope.length === 0) {
    throw new AmazonAdsTokenError(
      "Amazon Ads scope-narrowed: provider returned empty scope",
      { oauthError: "scope-narrowed" }
    );
  }
  const granted = scope.split(/\s+/).filter(Boolean);
  for (const required of AMAZON_ADS_SCOPES) {
    if (!granted.includes(required)) {
      throw new AmazonAdsTokenError(
        sanitizeAmazonAdsErrorMessage(
          `Amazon Ads scope-narrowed: required scope "${required}" not granted (received "${scope}")`
        ),
        { oauthError: "scope-narrowed" }
      );
    }
  }
}

/**
 * Token-prefix observability check. LWA emits `Atza|`-prefixed access tokens
 * and `Atzr|`-prefixed refresh tokens (per LWA spec). If either prefix is
 * absent, log a warning so future LWA prefix changes are visible — but do
 * NOT hard-fail. Defensive: Amazon may add new prefixes; we never want to
 * reject a legitimate token because of a prefix change.
 */
function logTokenPrefixObservability(data: AmazonAdsTokenResponse): void {
  const accessPrefixOk = data.access_token.startsWith("Atza|");
  const refreshPrefixOk =
    data.refresh_token === undefined || data.refresh_token.startsWith("Atzr|");
  if (!accessPrefixOk || !refreshPrefixOk) {
    console.warn(
      JSON.stringify({
        event: "amazon_ads_token_prefix_unexpected",
        accessPrefixOk,
        refreshPrefixOk,
        timestamp: new Date().toISOString(),
      })
    );
  }
}

// ============================================================================
// Callback Error Parsing
// ============================================================================

/**
 * Parses LWA OAuth callback errors from query params.
 *
 * LWA returns standard OAuth 2.0 error codes as `?error=...`. We map them to
 * user-facing messages and a retry hint per the documented LWA error codes.
 *
 * AC #14 distinct categories:
 *   - `unauthorized_client` → `lwa-app-draft-state` (Draft-state LWA app —
 *     non-allowlisted user attempted the flow; only test users can connect
 *     until the LWA app is promoted to Live)
 *   - `access_denied` → `user-cancelled`
 *   - `invalid_scope` → `unknown-scope` (typically a single-colon scope typo)
 */
export function parseAmazonAdsCallbackError(
  searchParams: URLSearchParams
): AmazonAdsCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "You denied the Amazon Ads connection request.",
      userAction: "retry",
      category: "user-cancelled",
    };
  }

  // AC #14 — Draft-state LWA-app distinct error.
  if (error === "unauthorized_client") {
    return {
      code: "oauth-config-error",
      userMessage:
        "Your Amazon Ads developer is using a Draft Login with Amazon (LWA) app — only allowlisted test users can connect. Ask your developer to promote the app to Live in the Amazon Developer Console at https://developer.amazon.com/loginwithamazon/console/site/lwa/overview.html (Security Profile → 'Web Settings' → 'Promote to Live'). This is a one-click change but requires LWA app review by Amazon (typical SLA: 1-3 business days).",
      userAction: "none",
      category: "lwa-app-draft-state",
    };
  }

  if (
    error === "invalid_request" ||
    error === "redirect_uri_mismatch" ||
    error === "unsupported_response_type" ||
    error === "unsupported_grant_type"
  ) {
    return {
      code: "oauth-config-error",
      userMessage:
        "Amazon Ads connection is misconfigured. Contact support.",
      userAction: "none",
      category: "config-error",
    };
  }

  if (error === "invalid_scope") {
    return {
      code: "oauth-config-error",
      userMessage:
        "Amazon Ads connection requested an unknown scope. Contact support — verify the scope `advertising::campaign_management` (DOUBLE colon) is configured.",
      userAction: "none",
      category: "unknown-scope",
    };
  }

  // LWA returns invalid_client on the callback when client credentials are
  // wrong — server-side config issue, NOT a customer-actionable problem.
  if (error === "invalid_client") {
    return {
      code: "oauth-config-error",
      userMessage:
        "Amazon Ads connection is misconfigured (server-side). Contact support.",
      userAction: "none",
      category: "config-error",
    };
  }

  if (error === "server_error" || error === "temporarily_unavailable") {
    return {
      code: "oauth-error",
      userMessage:
        "Amazon Ads is temporarily unavailable. Please try again later.",
      userAction: "retry",
      category: "transient",
    };
  }

  if (error === "invalid_grant") {
    // In the callback path, invalid_grant means the auth code expired or was
    // replayed. User-actionable: try connecting again.
    return {
      code: "oauth-error",
      userMessage:
        "Your Amazon sign-in session expired. Please try connecting again.",
      userAction: "retry",
      category: "session-expired",
    };
  }

  return {
    code: "oauth-error",
    userMessage:
      "An error occurred connecting to Amazon Ads. Please try again.",
    userAction: "retry",
    category: "unknown",
  };
}
